import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

const API_BASE_URL = '/api/v1';

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

class ApiError extends Error {
  status: number;
  response?: ApiResponse<unknown>;

  constructor(message: string, status: number, response?: ApiResponse<unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

// Deduplicates concurrent refresh calls so only one hits the backend at a time.
// Without this, token rotation causes race conditions: the first refresh revokes
// the old token, making all other in-flight refreshes fail with 401.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.data?.accessToken ?? null;
    } catch {
      return null;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Helper to get auth headers from stores
 */
function getAuthHeaders(): Record<string, string> {
  const { accessToken } = useAuthStore.getState();
  const { selectedTenantId } = useUiStore.getState();

  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;

  return headers;
}

/**
 * Execute a request with automatic 401 handling and token refresh.
 * If the request returns 401 and we have a token, attempt to refresh it and retry.
 */
async function withTokenRefresh<T>(
  makeRequest: (headers: Record<string, string>) => Promise<Response>,
  processResponse: (response: Response) => Promise<T>,
): Promise<T> {
  const { accessToken, setAccessToken, clearAuth } = useAuthStore.getState();
  const headers = getAuthHeaders();

  let response = await makeRequest(headers);

  // Handle 401 by attempting to refresh the token
  if (response.status === 401 && accessToken) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      setAccessToken(newToken);
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await makeRequest(headers);
    } else {
      // Refresh failed - session expired
      clearAuth();
      window.location.href = '/login';
      throw new ApiError('Session expired. Please log in again.', 401);
    }
  }

  return processResponse(response);
}

/**
 * Process a JSON API response, handling errors and parsing
 */
async function processJsonResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // Note: optional chaining on headers for test environment compatibility
  const contentType = response.headers?.get('content-type');

  let data: ApiResponse<T>;
  if (!contentType || contentType.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      throw new ApiError(
        response.status === 401 ? 'Authentication failed' : 'Request failed',
        response.status,
      );
    }
  } else {
    throw new ApiError(
      response.status === 401
        ? 'Authentication failed'
        : `Request failed with status ${response.status}`,
      response.status,
    );
  }

  if (!response.ok) {
    // PII Protection: Error responses may contain user data - backend should sanitize
    // Handle both string errors and object errors { message, code }
    let errorMessage = 'An error occurred';
    if (typeof data.error === 'string') {
      errorMessage = data.error;
    } else if (data.error && typeof data.error === 'object') {
      const errorObj = data.error as { message?: string };
      errorMessage = errorObj.message ?? errorMessage;
    }
    throw new ApiError(errorMessage, response.status, data);
  }

  return data;
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<ApiResponse<T>> {
  const { body: optionsBody, ...restOptions } = options;

  return withTokenRefresh(
    (headers) => {
      const fetchHeaders = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
        ...headers,
      };

      const fetchOptions: RequestInit = {
        ...restOptions,
        headers: fetchHeaders,
        credentials: 'include',
      };

      if (optionsBody) {
        fetchOptions.body = JSON.stringify(optionsBody);
      }

      return fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    },
    processJsonResponse<T>,
  );
}

/**
 * Post FormData with authentication (for file uploads)
 * Content-Type is NOT set - browser will set it with proper boundary
 */
async function postFormDataWithAuth<T>(
  endpoint: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  return withTokenRefresh(
    (headers) =>
      fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      }),
    processJsonResponse<T>,
  );
}

export const api = {
  get: <T>(endpoint: string, options?: FetchOptions) =>
    fetchWithAuth<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithAuth<T>(endpoint, { ...options, method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithAuth<T>(endpoint, { ...options, method: 'PATCH', body }),

  put: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithAuth<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: FetchOptions) =>
    fetchWithAuth<T>(endpoint, { ...options, method: 'DELETE' }),

  /**
   * Post FormData (for file uploads)
   * Content-Type is set automatically by browser to include boundary
   */
  postFormData: <T>(endpoint: string, formData: FormData) =>
    postFormDataWithAuth<T>(endpoint, formData),
};

/**
 * Fetch binary data (for file downloads) with auth and token refresh
 */
export async function fetchBlob(endpoint: string): Promise<Blob> {
  return withTokenRefresh(
    (headers) =>
      fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      }),
    async (response) => {
      if (!response.ok) {
        throw new ApiError(`Download failed with status ${response.status}`, response.status);
      }
      return response.blob();
    },
  );
}

/**
 * Fetch binary data together with the filename the server chose for it.
 *
 * `fetchBlob` hands back only the bytes, and an object URL made from a blob carries no
 * name -- so a caller that needs the server's filename cannot get it from there. This is
 * the sibling for those callers; `fetchBlob` itself is unchanged and stays the right
 * choice wherever the caller already knows the name (a document, an attachment).
 *
 * `filename` is null when the response carries no usable `Content-Disposition`. Picking a
 * fallback is deliberately left to the caller: this module is shared by every download in
 * the app, and a sensible default differs per caller.
 */
export async function fetchBlobWithFilename(
  endpoint: string,
): Promise<{ blob: Blob; filename: string | null }> {
  return withTokenRefresh(
    (headers) =>
      fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      }),
    async (response) => {
      if (!response.ok) {
        throw new ApiError(`Download failed with status ${response.status}`, response.status);
      }
      return {
        blob: await response.blob(),
        filename: filenameFromContentDisposition(response.headers.get('content-disposition')),
      };
    },
  );
}

/**
 * Read the `filename` parameter out of a `Content-Disposition` header, or null when there
 * is nothing usable in it. Only the plain `filename="..."` form is read: the exports in
 * this app slugify their names, so there is never anything for RFC 5987 to encode.
 */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // The parameter has to be its own: anchored to the start of the header or to a `;`, so
  // that `xfilename=` is not mistaken for `filename=`. `filename*=` (RFC 5987) does not
  // match either -- the exports here slugify their names, so there is nothing to decode,
  // and an unread name becomes null and lets the caller choose.
  const match = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  const raw = match?.[1] ?? match?.[2];
  const filename = raw?.trim();
  return filename ? filename : null;
}

/**
 * POST JSON and receive a binary response. Used by endpoints that build a file
 * server-side from a request body too large/structured for the URL (e.g. exports).
 *
 * On error, parses the JSON error envelope so the caller can show a useful message.
 */
export async function postBlob(endpoint: string, body: unknown): Promise<Blob> {
  return withTokenRefresh(
    (headers) =>
      fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
        credentials: 'include',
      }),
    async (response) => {
      if (!response.ok) {
        let errorMessage = `Download failed with status ${response.status}`;
        let errorResponse: ApiResponse<unknown> | undefined;
        try {
          errorResponse = await response.json();
          if (typeof errorResponse?.error === 'string') {
            errorMessage = errorResponse.error;
          } else if (errorResponse?.error && typeof errorResponse.error === 'object') {
            const errorObj = errorResponse.error as { message?: string };
            errorMessage = errorObj.message ?? errorMessage;
          }
        } catch {
          // Non-JSON error response; fall back to the generic message
        }
        throw new ApiError(errorMessage, response.status, errorResponse);
      }
      return response.blob();
    },
  );
}

export { ApiError };
